import type { ProjectSummary } from '@ai-meeting/shared'

/**
 * Cards per page. Eight fills the two-column grid four rows deep, which is
 * about one screen on a laptop — the point at which a longer list is scrolled
 * past rather than read.
 */
export const PROJECTS_PAGE_SIZE = 8

/**
 * Whether a project answers the search box.
 *
 * Matches the key as well as the name because the key is what people quote to
 * each other, and several names in a space differ only by a trailing word.
 *
 * @param query Raw box contents; blank matches everything.
 */
export function matchesProjectQuery(project: ProjectSummary, query: string): boolean {
  // No blank short-circuit: `includes('')` is true of every string, so an
  // empty box already matches everything without one.
  const needle = query.trim().toLowerCase()
  return (
    project.name.toLowerCase().includes(needle) ||
    project.projectKey.toLowerCase().includes(needle)
  )
}

/** A rendered page button, or the gap standing in for the pages between. */
export type PageSlot = number | 'gap'

/**
 * The page buttons to draw, 1-based.
 *
 * Up to five pages are listed outright. Beyond that it is a window of three
 * around the current page, with the first and last always reachable and a gap
 * where pages were left out — so the bar keeps a fixed width instead of growing
 * with the project count.
 *
 * @param page Current page, 1-based.
 * @param pageCount Total pages; `0` yields no buttons at all.
 * @example pageSlots(1, 8) // [1, 2, 3, 'gap', 8]
 */
export function pageSlots(page: number, pageCount: number): PageSlot[] {
  if (pageCount <= 5) {
    return Array.from({ length: Math.max(pageCount, 0) }, (_, index) => index + 1)
  }

  const start = Math.min(Math.max(page - 1, 1), pageCount - 2)
  const window = [start, start + 1, start + 2]
  const slots: PageSlot[] = []

  if (window[0] > 1) slots.push(1)
  if (window[0] > 2) slots.push('gap')
  slots.push(...window)
  if (window[2] < pageCount - 1) slots.push('gap')
  if (window[2] < pageCount) slots.push(pageCount)

  return slots
}

/**
 * The 1-based `start`–`end` the footer reports, clamped to what is actually
 * there. An empty list reports `0–0` rather than `1–0`.
 */
export function visibleRange(
  page: number,
  total: number,
  pageSize = PROJECTS_PAGE_SIZE
): { start: number; end: number } {
  if (total === 0) return { start: 0, end: 0 }
  const start = (page - 1) * pageSize + 1
  return { start, end: Math.min(page * pageSize, total) }
}
